/**
 * Helpers for the Cognito session Amplify keeps in localStorage.
 *
 * A session that Cognito will no longer refresh still looks like a session to
 * Amplify on the next page load: it reads the keys, tries to refresh, and only
 * gives up after several rejected requests. Clearing the keys once the refresh
 * has definitively failed keeps the next load fast instead of repeating the
 * whole round trip.
 */

const COGNITO_KEY_PREFIX = 'CognitoIdentityServiceProvider.';
const AMPLIFY_KEY_PREFIX = 'amplify-';

/**
 * localStorage throws rather than returning null when storage is disabled
 * (Chrome's "block all cookies", Safari lockdown profiles). Nothing here is
 * important enough to take the app down with it.
 */
const safely = (operation: () => void): void => {
  try {
    operation();
  } catch (error) {
    console.warn('authStorage: localStorage is unavailable', error);
  }
};

/**
 * Remove every Amplify/Cognito key, leaving the rest of localStorage alone.
 */
export const clearCognitoStorage = (): void => {
  safely(() => {
    const staleKeys = Object.keys(window.localStorage).filter(
      (key) => key.startsWith(COGNITO_KEY_PREFIX) || key.startsWith(AMPLIFY_KEY_PREFIX)
    );
    staleKeys.forEach((key) => window.localStorage.removeItem(key));
  });
};

/**
 * True when localStorage claims a signed-in user. Used to tell "first visit"
 * apart from "returning with a session that may or may not still be good".
 */
export const hasStoredCognitoSession = (): boolean => {
  try {
    return Object.keys(window.localStorage).some(
      (key) => key.startsWith(COGNITO_KEY_PREFIX) && key.endsWith('.LastAuthUser')
    );
  } catch (error) {
    return false;
  }
};
